import React, { useState, useRef, useEffect } from 'react';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  ChevronRight, 
  MapPin, 
  User, 
  Plus, 
  X, 
  Edit2, 
  Trash2, 
  Save, 
  Activity,
  ChevronLeft,
  Search,
  ArrowRight,
  Receipt,
  ExternalLink,
  Loader2,
  AlertCircle,
  Eye,
  ArrowLeft,
  Building,
  ShieldCheck,
  SendHorizontal,
  CheckCircle,
  ImageIcon,
  Tag,
  Camera,
  RefreshCw,
  ClipboardCheck,
  ClipboardList,
  FileSearch,
  History,
  Image as LucideImage,
  StickyNote,
  Info,
  Lock,
  Zap,
  Droplets,
  Wind,
  Warehouse,
  HardHat,
  Dog,
  Navigation,
  Scaling,
  Hash,
  Thermometer,
  CloudRain,
  Key,
  Layers,
  Map as MapIcon,
  Construction
} from 'lucide-react';
import { Inspection, Location, Contact, Note, NoteCategory, Invoice, Service, Agreement, InspectionImage, ImageTag, BusinessProfile, Inspector, Availability, ReportTemplate, InspectionReport, TimelineEvent } from '../types';
import { supabase, toSnakeCase, toCamelCase, getErrorMessage } from '../lib/supabase';

const MetadataChip = ({ icon: Icon, label, value, color = "olive", status = false }: any) => {
  if (!status && (value === undefined || value === null || value === false || value === '' || value === 0)) return null;
  
  let displayValue = value;
  let iconColorClass = "text-[#606c38]";
  let iconBgClass = "bg-[#606c38]/10";

  if (color === "amber") { iconColorClass = "text-amber-600"; iconBgClass = "bg-amber-50"; }
  if (color === "blue") { iconColorClass = "text-blue-600"; iconBgClass = "bg-blue-50"; }
  if (color === "red") { iconColorClass = "text-rose-600"; iconBgClass = "bg-rose-50"; }

  if (status) {
    displayValue = value ? 'ACTIVE' : 'OFFLINE';
    iconColorClass = value ? 'text-emerald-600' : 'text-rose-500';
    iconBgClass = value ? 'bg-emerald-50' : 'bg-rose-50';
  } else if (value === true) {
    displayValue = 'Yes';
  }

  return (
    <div className="px-3 py-2 bg-white border border-slate-200 rounded-xl flex items-center gap-3 shadow-sm transition-all hover:border-[#606c38]/30">
      <div className={`${iconBgClass} ${iconColorClass} p-1.5 rounded-lg shrink-0`}>
        <Icon size={12} strokeWidth={2.5} />
      </div>
      <div className="min-w-0">
        <p className="text-[8px] font-black text-slate-600 uppercase tracking-widest leading-none mb-1">{label}</p>
        <p className="text-[10px] font-bold text-slate-900 truncate leading-none uppercase">{displayValue}</p>
      </div>
    </div>
  );
};

const TimelineIcon = ({ type }: { type: TimelineEvent['type'] }) => {
  switch (type) {
    case 'Created': return <Plus size={14} />;
    case 'StatusChanged': return <Activity size={14} />;
    case 'AgreementSent': return <SendHorizontal size={14} />;
    case 'AgreementSigned': return <ShieldCheck size={14} />;
    case 'FileUploaded': return <Camera size={14} />;
    case 'NoteAdded': return <StickyNote size={14} />;
    default: return <CheckCircle size={14} />;
  }
};

// Fix: Defined InspectionsProps interface to resolve build error
interface InspectionsProps {
  inspections: Inspection[];
  setInspections: React.Dispatch<React.SetStateAction<Inspection[]>>;
  properties: Location[];
  contacts: Contact[];
  notes: Note[];
  setNotes: React.Dispatch<React.SetStateAction<Note[]>>;
  categories: NoteCategory[];
  invoices: Invoice[];
  setInvoices: React.Dispatch<React.SetStateAction<Invoice[]>>;
  onCreateInvoice: (inspectionId: string) => void;
  onNavigateToLocation?: (locationId: string) => void;
  initialInspectionId: string | null;
  onClearNavigation?: () => void;
  services: Service[];
  agreements: Agreement[];
  setAgreements: React.Dispatch<React.SetStateAction<Agreement[]>>;
  imageTags: ImageTag[];
  setImageTags: React.Dispatch<React.SetStateAction<ImageTag[]>>;
  businessProfile: BusinessProfile;
  inspectors: Inspector[];
  availabilities: Availability[];
  onOpenReportEditor: (id: string) => void;
  reports: InspectionReport[];
  setReports: React.Dispatch<React.SetStateAction<InspectionReport[]>>;
}

const Inspections: React.FC<InspectionsProps> = ({ 
  inspections, setInspections, properties: locations, contacts, notes, setNotes, categories, invoices, setInvoices, onCreateInvoice, onNavigateToLocation, initialInspectionId, onClearNavigation, services, agreements, setAgreements, imageTags, setImageTags, businessProfile, inspectors, availabilities, onOpenReportEditor, reports, setReports
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInspectionId, setSelectedInspectionId] = useState<string | null>(initialInspectionId || null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isSendingBatch, setIsSendingBatch] = useState(false);
  const [isSyncingAgreements, setIsSyncingAgreements] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>([]);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  useEffect(() => {
    if (initialInspectionId) {
      setSelectedInspectionId(initialInspectionId);
      if (onClearNavigation) onClearNavigation();
    }
  }, [initialInspectionId, onClearNavigation]);

  useEffect(() => {
    if (selectedInspectionId) fetchTemplates();
  }, [selectedInspectionId]);

  const fetchTemplates = async () => {
    const { data } = await supabase.from('report_templates').select('*');
    if (data) setReportTemplates(data.map(toCamelCase));
  };

  const handleSyncAgreements = async () => {
    if (!selectedInspectionId) return;
    setIsSyncingAgreements(true);
    try {
      const { data: insData } = await supabase.from('inspections').select('*').eq('id', selectedInspectionId).single();
      const ins = toCamelCase(insData);
      const { data: invoiceData } = await supabase.from('invoices').select('*').eq('inspection_id', selectedInspectionId).maybeSingle();
      const inv = invoiceData ? toCamelCase(invoiceData) : null;
      
      const serviceIds = inv ? inv.items.map((i: any) => i.serviceId).filter(Boolean) : [];
      const { data: templates } = await supabase.from('agreement_templates').select('*').in('service_id', serviceIds);
      
      if (!templates || templates.length === 0) {
        alert("No protocol templates mapped to the active ledger items.");
        return;
      }

      for (const t of templates) {
        const existing = agreements.find(a => a.inspectionId === selectedInspectionId && a.agreementTemplateId === t.id);
        if (existing) continue;

        let content = t.content;
        const contact = getContact(ins.contactId);
        const prop = getProperty(ins.propertyId);
        
        content = content.replace(/\[CLIENT_NAME\]/g, contact?.name || 'Client');
        content = content.replace(/\[ADDRESS\]/g, prop?.address || 'Subject Property');
        content = content.replace(/\[FEE\]/g, inv?.total?.toString() || '0.00');
        content = content.replace(/\[DATE\]/g, ins.date);

        const { data: newAgr, error } = await supabase.from('agreements').insert([{
          inspection_id: selectedInspectionId,
          agreement_template_id: t.id,
          title: t.title,
          content: content,
          status: 'Draft'
        }]).select().single();
        
        if (!error && newAgr) {
          setAgreements(prev => [toCamelCase(newAgr), ...prev]);
        }
      }
      alert("Compliance registry synchronized.");
    } catch (err) {
      alert(getErrorMessage(err));
    } finally {
      setIsSyncingAgreements(false);
    }
  };

  const handleDeleteAgreement = async (id: string) => {
    if (!confirm('Purge this protocol record from the registry?')) return;
    try {
      await supabase.from('agreements').delete().eq('id', id);
      setAgreements(prev => prev.filter(a => a.id !== id));
    } catch (err) { alert(getErrorMessage(err)); }
  };

  const provisionReport = async (template: ReportTemplate) => {
    if (!selectedInspectionId) return;
    setIsProvisioning(true);
    try {
      const payload = {
        inspection_id: selectedInspectionId,
        template_id: template.id,
        answers: { _structure: template.structure }, 
        status: 'Draft'
      };
      const { data, error } = await supabase.from('inspection_reports').insert([payload]).select().single();
      if (error) throw error;
      
      const newReport = toCamelCase(data);
      setReports(prev => [newReport, ...prev]);
      setShowTemplatePicker(false);
      onOpenReportEditor(newReport.id);
    } catch (err) { alert(getErrorMessage(err)); }
    finally { setIsProvisioning(false); }
  };

  const handleRegenerateReport = async (report: InspectionReport) => {
    if (!confirm('WARNING: Regnerating will purge ALL field data recorded in this audit. Proceed?')) return;
    setIsProvisioning(true);
    try {
      await supabase.from('inspection_reports').delete().eq('id', report.id);
      const { data: latestTmpl } = await supabase.from('report_templates').select('*').eq('id', report.templateId).single();
      if (!latestTmpl) throw new Error("Source template no longer exists.");
      
      const payload = {
        inspection_id: selectedInspectionId,
        template_id: latestTmpl.id,
        answers: { _structure: latestTmpl.structure },
        status: 'Draft'
      };
      const { data, error } = await supabase.from('inspection_reports').insert([payload]).select().single();
      if (error) throw error;
      
      const updatedReport = toCamelCase(data);
      setReports(prev => prev.map(r => r.id === report.id ? updatedReport : r));
      alert("Snapshot Resynchronized.");
    } catch (err) { alert(getErrorMessage(err)); }
    finally { setIsProvisioning(false); }
  };

  const [formData, setFormData] = useState<Partial<Inspection>>({
    propertyId: '', contactId: '', inspectorId: '', date: new Date().toISOString().split('T')[0], time: '10:00', status: 'Scheduled',
    metadata: { 
      inspectionType: 'Standard', weatherConditions: 'Clear', occupancyAtInspection: 'Vacant', lockboxUsed: false, utilitiesVerified: true, reportUrgency: 'Normal'
    }
  });

  const selectedInspection = inspections.find(i => i.id === selectedInspectionId);
  const selectedProperty = locations.find(l => l.id === selectedInspection?.propertyId);
  const selectedContact = contacts.find(c => c.id === selectedInspection?.contactId);
  const selectedInspector = inspectors.find(i => i.id === selectedInspection?.inspectorId);
  const inspectionAgreements = agreements.filter(a => a.inspectionId === selectedInspectionId);
  const sessionInvoice = invoices.find(inv => inv.inspectionId === selectedInspectionId);
  const hasUnsignedAgreements = inspectionAgreements.some(a => a.status !== 'Signed');
  const sessionReports = reports.filter(r => r.inspectionId === selectedInspectionId);

  const getContact = (id: string) => contacts.find(c => c.id === id);
  const getProperty = (id: string) => locations.find(l => l.id === id);

  const filteredInspections = inspections.filter(i => {
    const prop = getProperty(i.propertyId);
    const cont = getContact(i.contactId);
    const search = searchTerm.toLowerCase();
    return (prop?.address || '').toLowerCase().includes(search) || 
           (cont?.name || '').toLowerCase().includes(search);
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.propertyId || !formData.contactId) return;
    setSaving(true);
    try {
      // FIX: Destructure to remove non-database column fields and managed system fields
      const { images, timeline, files, id, createdAt, ...coreData } = formData;
      const payload = toSnakeCase(coreData);
      
      if (editingId) {
        const { error } = await supabase.from('inspections').update(payload).eq('id', editingId);
        if (error) throw error;
        setInspections(prev => prev.map(i => i.id === editingId ? { ...i, ...formData } as Inspection : i));
      } else {
        const { data, error } = await supabase.from('inspections').insert([payload]).select().single();
        if (error) throw error;
        const newIns = toCamelCase(data);
        setInspections(prev => [newIns, ...prev]);
        setSelectedInspectionId(newIns.id);
      }
      setIsSidebarOpen(false);
    } catch (err) { alert(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && selectedInspectionId) {
      setUploadingImage(true);
      try {
        const fileName = `${selectedInspectionId}/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
        const { error: uploadError } = await supabase.storage.from('inspection-photos').upload(fileName, file);
        if (uploadError) throw uploadError;
        const { data: { publicUrl } } = supabase.storage.from('inspection-photos').getPublicUrl(fileName);
        const { data: imgRecord, error: dbError } = await supabase.from('inspection_images').insert([{
          inspection_id: selectedInspectionId,
          url: publicUrl,
          description: '',
          tag_id: imageTags[0]?.id || ''
        }]).select().single();
        if (dbError) throw dbError;
        
        const newImage = toCamelCase(imgRecord);
        setInspections(prev => prev.map(i => i.id === selectedInspectionId ? { ...i, images: [...(i.images || []), newImage] } : i));
      } catch (err) { alert(getErrorMessage(err)); }
      finally { setUploadingImage(false); }
    }
  };

  const handleDeleteImage = async (imgId: string) => {
    if (!confirm('Purge this media artifact?')) return;
    try {
       const { error } = await supabase.from('inspection_images').delete().eq('id', imgId);
       if (error) throw error;
       setInspections(prev => prev.map(i => i.id === selectedInspectionId ? { ...i, images: i.images?.filter(img => img.id !== imgId) } : i));
    } catch (err) { alert(getErrorMessage(err)); }
  };

  const handleDispatchBatch = async () => {
    if (!selectedInspection) return;
    const contact = getContact(selectedInspection.contactId);
    if (!contact?.email) return alert("Stakeholder email required.");
    setIsSendingBatch(true);
    try {
       await supabase.functions.invoke('smtp2go', {
          body: {
             to: contact.email,
             customerName: contact.name,
             propertyAddress: selectedProperty?.address || "Subject Property",
             paymentUrl: `${window.location.origin}/portal/${selectedInspection.id}`,
             emailType: 'agreements_ready',
             businessName: businessProfile.companyName
          }
       });
       const unsignedIds = inspectionAgreements.filter(a => a.status !== 'Signed').map(a => a.id);
       if (unsignedIds.length > 0) {
          await supabase.from('agreements').update({ status: 'Sent' }).in('id', unsignedIds);
          setAgreements(prev => prev.map(a => unsignedIds.includes(a.id) ? { ...a, status: 'Sent' } : a));
       }
       alert("Email protocols transmitted.");
    } catch (err) { alert(getErrorMessage(err)); }
    finally { setIsSendingBatch(false); }
  };

  const handleDelete = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!confirm('Permanently purge this session?')) return;
    try {
      const { error } = await supabase.from('inspections').delete().eq('id', id);
      if (error) throw error;
      setInspections(prev => prev.filter(i => i.id !== id));
      if (selectedInspectionId === id) setSelectedInspectionId(null);
    } catch (err) { alert(getErrorMessage(err)); }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Scheduled': return 'bg-blue-50 text-blue-600';
      case 'In Progress': return 'bg-amber-50 text-amber-600';
      case 'Completed': 
      case 'Visited': return 'bg-emerald-50 text-emerald-600';
      case 'Cancelled': 
      case 'Skipped': return 'bg-rose-50 text-rose-600';
      default: return 'bg-slate-50 text-slate-400';
    }
  };

  return (
    <div className="animate-in fade-in duration-300">
      {selectedInspectionId && selectedInspection ? (
        <div className="space-y-6 pb-20 animate-in slide-in-from-right-2">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm gap-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setSelectedInspectionId(null)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 transition-all"><ChevronLeft size={20} /></button>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-black text-slate-900 uppercase tracking-tighter leading-none">{selectedProperty?.address || 'Field Session'}</h3>
                  <button 
                    onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedProperty?.address + ', ' + selectedProperty?.city)}`, '_blank')}
                    className="p-1 text-slate-300 hover:text-blue-500 transition-colors"
                  >
                    <MapIcon size={14} />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${getStatusColor(selectedInspection.status)}`}>{selectedInspection.status}</span>
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">• {selectedInspection.date} @ {selectedInspection.time}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
               <button onClick={() => window.open(`${window.location.origin}/portal/${selectedInspectionId}`, '_blank')} className="flex-1 md:flex-none px-4 py-2 bg-blue-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg"><ExternalLink size={12} /> Client Hub</button>
               <button onClick={() => { setEditingId(selectedInspectionId); setFormData(selectedInspection); setIsSidebarOpen(true); }} className="flex-1 md:flex-none px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all flex items-center justify-center gap-2"><Edit2 size={12} /> Edit</button>
               <button onClick={(e) => handleDelete(selectedInspectionId, e)} className="p-2 bg-rose-50 text-rose-600 rounded-lg hover:bg-rose-100 transition-all"><Trash2 size={16} /></button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
             <div className="lg:col-span-8 space-y-8">
                {/* ASSET INTEL PANEL */}
                {selectedProperty && (
                  <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-500">
                    <div className="p-5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                       <div className="flex items-center gap-2">
                          <Building size={16} className="text-[#606c38]" />
                          <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Asset Intelligence Registry</h4>
                       </div>
                       <button onClick={() => onNavigateToLocation?.(selectedProperty.id)} className="text-[9px] font-black text-[#606c38] uppercase tracking-widest hover:underline flex items-center gap-1">Full Record <ChevronRight size={10} /></button>
                    </div>
                    <div className="p-6">
                       <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                          <div className="space-y-4">
                             <div className="flex items-center gap-2 mb-2 border-b border-slate-50 pb-1">
                                <Key size={12} className="text-blue-500" />
                                <span className="text-[9px] font-black uppercase text-slate-400">Entry Protocol</span>
                             </div>
                             <div className="grid grid-cols-1 gap-2">
                                <MetadataChip icon={Lock} label="Lockbox" value={selectedProperty.metadata?.lockboxType} />
                                <MetadataChip icon={Hash} label="Code" value={selectedProperty.metadata?.lockboxCode} color="blue" />
                                <MetadataChip icon={ShieldCheck} label="Alarm" value={selectedProperty.metadata?.alarmSystemPresent} />
                                <MetadataChip icon={Navigation} label="Gate" value={selectedProperty.metadata?.gateAccessRequired} />
                                <MetadataChip icon={Dog} label="Pets" value={selectedProperty.metadata?.petsPresent} color="amber" />
                             </div>
                          </div>
                          <div className="space-y-4">
                             <div className="flex items-center gap-2 mb-2 border-b border-slate-50 pb-1">
                                <Zap size={12} className="text-amber-500" />
                                <span className="text-[9px] font-black uppercase text-slate-400">Systems Status</span>
                             </div>
                             <div className="grid grid-cols-1 gap-2">
                                <MetadataChip icon={Zap} label="Electric" value={selectedProperty.metadata?.utilitiesOnElectric} status />
                                <MetadataChip icon={Droplets} label="Water" value={selectedProperty.metadata?.utilitiesOnWater} status />
                                <MetadataChip icon={Wind} label="Gas/Fuel" value={selectedProperty.metadata?.utilitiesOnGas} status />
                                <MetadataChip icon={Thermometer} label="A/C Units" value={selectedProperty.metadata?.acUnitCount} />
                                <MetadataChip icon={CloudRain} label="Sprinklers" value={selectedProperty.metadata?.sprinklerSystemPresent} />
                             </div>
                          </div>
                          <div className="space-y-4">
                             <div className="flex items-center gap-2 mb-2 border-b border-slate-50 pb-1">
                                <Layers size={12} className="text-[#606c38]" />
                                <span className="text-[9px] font-black uppercase text-slate-400">Technical Profile</span>
                             </div>
                             <div className="grid grid-cols-1 gap-2">
                                <MetadataChip icon={Scaling} label="Area" value={`${selectedProperty.squareFeet?.toLocaleString()} SF`} />
                                <MetadataChip icon={Construction} label="Built" value={selectedProperty.yearBuilt} />
                                <MetadataChip icon={Warehouse} label="Basement" value={selectedProperty.metadata?.basementPresent} />
                                <MetadataChip icon={HardHat} label="Crawlspace" value={selectedProperty.metadata?.crawlspacePresent} />
                                <MetadataChip icon={AlertCircle} label="Risks" value={selectedProperty.metadata?.knownWaterIntrusionHistory ? 'Water Hist.' : null} color="red" />
                             </div>
                          </div>
                       </div>
                       
                       {selectedProperty.metadata?.accessInstructions && (
                         <div className="mt-6 p-4 bg-blue-50/50 border border-blue-100 rounded-xl">
                            <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Info size={10} /> Dispatch Notes</p>
                            <p className="text-xs text-slate-600 font-medium leading-relaxed italic">"{selectedProperty.metadata.accessInstructions}"</p>
                         </div>
                       )}
                    </div>
                  </section>
                )}

                {/* REPORTS */}
                <section className="space-y-4">
                   <div className="flex items-center justify-between px-1">
                      <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-widest flex items-center gap-2"><ClipboardCheck size={14} className="text-[#606c38]" /> Registry Reports</h4>
                      <button onClick={() => setShowTemplatePicker(true)} className="text-[9px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1.5 hover:underline"><Plus size={12} /> New Audit</button>
                   </div>
                   <div className="grid grid-cols-1 gap-3">
                      {sessionReports.map(rpt => (
                        <div key={rpt.id} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between group hover:border-[#606c38] transition-all">
                           <div className="flex items-center gap-4">
                              <div className="p-3 bg-slate-50 text-slate-400 rounded-xl group-hover:bg-[#606c38]/10 group-hover:text-[#606c38] transition-colors"><FileSearch size={22} /></div>
                              <div>
                                 <h5 className="font-black text-slate-900 text-sm uppercase tracking-tight">{reportTemplates.find(t => t.id === rpt.templateId)?.name || 'Field Audit'}</h5>
                                 <div className="flex items-center gap-2 mt-1">
                                    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-tighter ${rpt.status === 'Completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>{rpt.status}</span>
                                    <span className="text-[9px] text-slate-300 font-bold tracking-widest">• {new Date(rpt.createdAt).toLocaleDateString()}</span>
                                 </div>
                              </div>
                           </div>
                           <div className="flex gap-2">
                              <button onClick={() => handleRegenerateReport(rpt)} className="p-2 text-slate-200 hover:text-amber-600 transition-colors" title="Sync Blueprint"><RefreshCw size={16} /></button>
                              <button onClick={() => onOpenReportEditor(rpt.id)} className="px-5 py-2 bg-slate-900 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2 shadow-md">Enter Field Audit <ArrowRight size={14} /></button>
                           </div>
                        </div>
                      ))}
                      {sessionReports.length === 0 && (
                        <div className="py-12 text-center bg-white rounded-2xl border-2 border-dashed border-slate-100">
                           <ClipboardList size={32} className="mx-auto text-slate-100 mb-3" />
                           <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">No reports provisioned</p>
                        </div>
                      )}
                   </div>
                </section>

                {/* MEDIA ARCHIVE */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                     <div className="flex items-center gap-2">
                        <LucideImage size={16} className="text-[#606c38]" />
                        <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Field Media Registry</h4>
                     </div>
                     <div>
                        <button disabled={uploadingImage} onClick={() => imageInputRef.current?.click()} className="px-4 py-1.5 bg-slate-900 text-white rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-slate-800 transition-all shadow-md">
                          {uploadingImage ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />} Capture Asset
                        </button>
                        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                     </div>
                  </div>
                  <div className="p-6">
                     {selectedInspection.images && selectedInspection.images.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                           {selectedInspection.images.map((img) => (
                             <div key={img.id} className="relative group aspect-square bg-slate-50 rounded-xl border border-slate-100 overflow-hidden shadow-sm transition-all hover:shadow-md">
                                <img src={img.url} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt="Field Asset" />
                                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/40 transition-colors flex items-center justify-center gap-2">
                                   <button onClick={() => window.open(img.url)} className="p-2 bg-white rounded-lg text-[#606c38] scale-90 group-hover:scale-100 opacity-0 group-hover:opacity-100 transition-all"><Eye size={14} /></button>
                                   <button onClick={() => handleDeleteImage(img.id)} className="p-2 bg-white rounded-lg text-rose-500 scale-90 group-hover:scale-100 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={14} /></button>
                                </div>
                             </div>
                           ))}
                        </div>
                     ) : (
                        <div className="py-12 text-center border-2 border-dashed border-slate-100 rounded-xl bg-slate-50/30">
                           <Camera size={32} className="mx-auto text-slate-200 mb-2" />
                           <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">No field media indexed</p>
                        </div>
                     )}
                  </div>
                </div>

                {/* TIMELINE */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                   <div className="p-5 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
                      <History size={16} className="text-[#606c38]" />
                      <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Session log</h4>
                   </div>
                   <div className="p-6">
                      <div className="space-y-6 relative before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-px before:bg-slate-100">
                         {(selectedInspection.timeline || []).length > 0 ? (selectedInspection.timeline || []).slice().reverse().map((event) => (
                            <div key={event.id} className="relative pl-8 animate-in slide-in-from-left-2">
                               <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 z-10 shadow-sm">
                                  <TimelineIcon type={event.type} />
                               </div>
                               <div>
                                  <div className="flex justify-between items-start">
                                     <p className="text-xs font-bold text-slate-800 leading-none">{event.description}</p>
                                     <span className="text-[9px] font-black text-slate-300 uppercase">{new Date(event.timestamp).toLocaleDateString()}</span>
                                  </div>
                                  <p className="text-[10px] text-slate-400 font-medium mt-1 uppercase tracking-tighter">{event.type} • {new Date(event.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                               </div>
                            </div>
                         )) : <div className="text-center py-4"><p className="text-[9px] font-black text-slate-300 uppercase">Creation log pending</p></div>}
                      </div>
                   </div>
                </div>
             </div>

             <aside className="lg:col-span-4 space-y-6">
                <div className="bg-[#606c38] rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
                   <div className="absolute top-0 right-0 p-8 text-white/5 -rotate-12 pointer-events-none"><ShieldCheck size={100} /></div>
                   <div className="relative z-10 space-y-6">
                      <div>
                        <h4 className="text-[9px] font-black text-white/50 uppercase tracking-widest mb-1.5">Stakeholder</h4>
                        <p className="text-lg font-black tracking-tight">{selectedContact?.name || 'Unassigned'}</p>
                        <p className="text-[10px] font-bold text-slate-200 truncate uppercase">{selectedContact?.email}</p>
                      </div>
                      <div>
                        <h4 className="text-[9px] font-black text-white/50 uppercase tracking-widest mb-1.5">Technician</h4>
                        <p className="text-lg font-black tracking-tight">{selectedInspector?.name || 'Not Dispatched'}</p>
                        <p className="text-[10px] font-bold text-slate-200 uppercase tracking-widest">Badge: {selectedInspector?.licenseNumber || '--'}</p>
                      </div>
                   </div>
                </div>

                {/* COMPLIANCE REGISTRY */}
                <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                   <div className="p-6 bg-slate-50 border-b border-slate-100 text-center">
                      <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-4 flex items-center justify-center gap-2"><ShieldCheck size={16} /> Compliance Vault</h4>
                      <div className="flex flex-col gap-2">
                        {hasUnsignedAgreements && (
                          <button disabled={isSendingBatch} onClick={handleDispatchBatch} className="w-full px-4 py-2 bg-[#606c38] text-white rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-[#283618] transition-all shadow-md">
                            {isSendingBatch ? <Loader2 size={12} className="animate-spin" /> : <SendHorizontal size={12} />} Dispatch Batch
                          </button>
                        )}
                        <button disabled={isSyncingAgreements} onClick={handleSyncAgreements} className="w-full px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-slate-50 transition-all">
                          {isSyncingAgreements ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Sync Protocols
                        </button>
                      </div>
                   </div>
                   <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto no-scrollbar">
                      {inspectionAgreements.length > 0 ? inspectionAgreements.map(a => (
                        <div key={a.id} className="p-5 flex items-center justify-between gap-4 group">
                           <div className="min-w-0">
                              <p className="text-xs font-bold text-slate-800 truncate mb-1">{a.title}</p>
                              <span className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-tighter ${a.status === 'Signed' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>{a.status}</span>
                           </div>
                           <button onClick={() => handleDeleteAgreement(a.id)} className="p-1.5 text-slate-200 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={14} /></button>
                        </div>
                      )) : <div className="p-10 text-center text-[10px] font-black text-slate-300 uppercase tracking-widest">No protocols indexed</div>}
                   </div>
                </section>

                {/* FINANCIAL LEDGER */}
                <section className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden shadow-xl text-white">
                   <div className="p-6 border-b border-white/5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Receipt size={16} className="text-indigo-400" />
                        <h4 className="text-[10px] font-black text-slate-200 uppercase tracking-widest">Ledger</h4>
                      </div>
                      {!sessionInvoice && <button onClick={() => onCreateInvoice(selectedInspectionId || '')} className="text-[9px] font-black text-indigo-400 uppercase hover:text-indigo-300">Draft Ledger</button>}
                   </div>
                   <div className="p-8">
                      {sessionInvoice ? (
                        <div className="space-y-6">
                           <div className="flex justify-between items-end">
                              <div>
                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Registry Balance</p>
                                <p className="text-3xl font-black text-white tracking-tighter">${sessionInvoice.total.toLocaleString()}</p>
                              </div>
                              <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${sessionInvoice.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>{sessionInvoice.status}</span>
                           </div>
                           <button onClick={() => onCreateInvoice(selectedInspectionId || '')} className="w-full py-3 bg-indigo-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg flex items-center justify-center gap-2">
                             Access Ledger
                           </button>
                        </div>
                      ) : <div className="text-center py-4"><p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">No entries found</p></div>}
                   </div>
                </section>
             </aside>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
             <div><h3 className="text-lg font-black text-slate-800 tracking-tight uppercase">Audit Registry</h3></div>
             <button onClick={() => { setEditingId(null); setFormData({propertyId: '', contactId: '', inspectorId: '', date: new Date().toISOString().split('T')[0], time: '10:00', status: 'Scheduled', metadata: { inspectionType: 'Standard', weatherConditions: 'Clear', occupancyAtInspection: 'Vacant', lockboxUsed: false, utilitiesVerified: true, reportUrgency: 'Normal' }}); setIsSidebarOpen(true); }} className="bg-slate-900 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-slate-800 transition-all flex items-center justify-center gap-2"><Plus size={18} /> New Session</button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto no-scrollbar">
             <table className="w-full text-left min-w-max">
                <thead><tr className="bg-slate-50/50 border-b border-slate-200"><th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Asset & Schedule</th><th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Stakeholder</th><th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">Status</th><th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest text-right">Actions</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                   {filteredInspections.map(ins => {
                     const prop = getProperty(ins.propertyId);
                     const cont = getContact(ins.contactId);
                     return (
                       <tr key={ins.id} onClick={() => setSelectedInspectionId(ins.id)} className="hover:bg-slate-50/80 transition-colors cursor-pointer group">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 overflow-hidden shadow-sm">
                                {prop?.image ? <img src={prop.image} className="w-full h-full object-cover" /> : <CalendarIcon size={18} />}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-slate-800 truncate max-w-[250px]">{prop?.address || 'Audit'}</p>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">{ins.date}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4"><p className="text-xs font-bold text-slate-700">{cont?.name || '--'}</p></td>
                          <td className="px-6 py-4 text-center"><span className={`text-[8px] font-black px-2 py-0.5 rounded uppercase tracking-wider ${getStatusColor(ins.status)}`}>{ins.status}</span></td>
                          <td className="px-6 py-4 text-right"><ChevronRight size={18} className="text-slate-200 inline-block" /></td>
                       </tr>
                     );
                   })}
                </tbody>
             </table>
          </div>
        </div>
      )}

      {/* Editor Sidebar */}
      <div className={`fixed inset-0 z-[300] ${isSidebarOpen ? 'visible' : 'invisible'}`}>
        <div className="absolute inset-0 bg-slate-900/40 transition-opacity duration-300" onClick={() => setIsSidebarOpen(false)} />
        <div className={`absolute inset-y-0 right-0 w-full md:max-w-xl bg-white shadow-2xl transition-transform duration-500 ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
             <h3 className="font-bold text-slate-800 text-xs uppercase tracking-widest">{editingId ? 'Modify Session Plan' : 'Add Field Session'}</h3>
             <button onClick={() => setIsSidebarOpen(false)} className="p-2 hover:bg-white rounded-lg text-slate-400 transition-all"><X size={24} /></button>
          </div>
          <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 no-scrollbar bg-white">
             <div className="space-y-6">
                <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Target Asset</label><select required className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-sm focus:ring-1 focus:ring-[#606c38] outline-none" value={formData.propertyId} onChange={(e) => setFormData({...formData, propertyId: e.target.value})}><option value="">Select property...</option>{locations.map(l => <option key={l.id} value={l.id}>{l.address}</option>)}</select></div>
                <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Stakeholder</label><select required className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-sm focus:ring-1 focus:ring-[#606c38] outline-none" value={formData.contactId} onChange={(e) => setFormData({...formData, contactId: e.target.value})}><option value="">Select contact...</option>{contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                <div className="grid grid-cols-2 gap-4">
                   <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Audit Date</label><input required type="date" className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-sm focus:ring-1 focus:ring-[#606c38] outline-none" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
                   <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Schedule Time</label><input required type="time" className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-sm focus:ring-1 focus:ring-[#606c38] outline-none" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} /></div>
                </div>
                <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Field Technician</label><select className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-sm focus:ring-1 focus:ring-[#606c38] outline-none" value={formData.inspectorId} onChange={(e) => setFormData({...formData, inspectorId: e.target.value})}><option value="">Unassigned</option>{inspectors.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}</select></div>
                <div><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-2">Initial Status</label><select className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 text-sm focus:ring-1 focus:ring-[#606c38] outline-none" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value as any})}><option value="Scheduled">Scheduled</option><option value="In Progress">In Progress</option><option value="Completed">Completed</option><option value="Visited">Visited</option><option value="Skipped">Skipped</option><option value="Cancelled">Cancelled</option></select></div>
             </div>
          </form>
          <div className="p-6 md:p-8 border-t border-slate-100 flex gap-4 bg-white">
             <button type="button" onClick={() => setIsSidebarOpen(false)} className="flex-1 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 rounded-xl border border-slate-200 transition-all">Cancel</button>
             <button disabled={saving} type="submit" onClick={handleSave} className="flex-[2] py-4 bg-slate-900 text-white font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-xl">{saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Commit Registry</button>
          </div>
        </div>
      </div>

      {/* Template Picker Overlay */}
      {showTemplatePicker && (
        <div className="fixed inset-0 z-[600] flex items-center justify-center p-6">
           <div className="absolute inset-0 bg-slate-900/60 transition-opacity" onClick={() => setShowTemplatePicker(false)} />
           <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl relative overflow-hidden flex flex-col max-h-[80vh] animate-in zoom-in-95">
              <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                 <div className="flex items-center gap-3"><div className="p-2.5 bg-[#606c38] text-white rounded-xl shadow-lg"><ClipboardCheck size={20} /></div><h3 className="font-black text-slate-900 text-sm uppercase tracking-widest">Provision Field Audit</h3></div>
                 <button onClick={() => setShowTemplatePicker(false)} className="p-2 hover:bg-white rounded-full text-slate-400 transition-colors"><X size={24} /></button>
              </div>
              <div className="p-8 space-y-4 flex-1 overflow-y-auto no-scrollbar">
                 {reportTemplates.map(t => (
                   <button key={t.id} onClick={() => provisionReport(t)} className="w-full text-left p-6 bg-slate-50 border border-slate-200 rounded-2xl hover:bg-white hover:border-[#606c38] hover:shadow-xl transition-all group flex items-center justify-between">
                      <div><h5 className="font-black text-slate-900 text-sm uppercase tracking-tight">{t.name}</h5><p className="text-[10px] text-slate-400 font-bold uppercase mt-1">{t.structure.length} Flow Sections</p></div>
                      <ChevronRight size={18} className="text-slate-300 group-hover:text-[#606c38] transition-colors" />
                   </button>
                 ))}
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default Inspections;